import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import produce from 'immer';
import React, {ReactNode} from 'react';

import FieldError from 'ui-library/FieldError';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import {IntermediateValueControlledNumericInput} from 'ui-library/inputs/ControlledNumericInput';

import {RegressionParameterConstraintType} from '../RegressionTypes';
import {
  CUSTOM_INPUT_PROPERTIES,
  EMPTY_NUMERIC_INPUT_VALUE,
  PARAMETER_NAME_TO_READABLE,
} from './RegressionConfigModalConstants';
import {getParameterValueErrorMessage} from './regressionConfigModalStateIsValid';
import {
  RegressionConfigModalState,
  RegressionConfigModalParameterInitialValue,
} from './RegressionConfigModalTypes';

interface ParameterInitialValueEditorRowProps {
  labelContent: ReactNode;
  checkboxContent: ReactNode;
  inputContent: ReactNode;
  errorContent: ReactNode;
  className: 'u-input-spacing-top' | 'u-small-spacing-top';
}

const CANNOT_SET_INITIAL_VALUE_TOOLTIP =
  'Initial value cannot be set because this parameter is already constrained to a value.';

/**
 * Helper component to give HTML scaffolding for each row of the RegressionConfigInitialValuesEditor.
 */
export function InitialValuesEditorRow({
  labelContent,
  checkboxContent,
  inputContent,
  errorContent,
  className,
}: ParameterInitialValueEditorRowProps): JSX.Element {
  return (
    <>
      <div className={classNames(css(styles.rowWrapper), 'u-vertical-center-items', className)}>
        <div className={css(styles.label)}>{labelContent}</div>
        <div className={css(styles.checkbox)}>{checkboxContent}</div>
        <div className={css(styles.input)}>{inputContent}</div>
      </div>
      {errorContent}
    </>
  );
}

export interface RegressionConfigInitialValuesEditorProps {
  regressionConfigState: RegressionConfigModalState;
  onChangeRegressionConfigState: (newState: RegressionConfigModalState) => void;
}

export default function RegressionConfigInitialValuesEditor(
  props: RegressionConfigInitialValuesEditorProps
): JSX.Element {
  return (
    <>
      <InitialValuesEditorRow
        labelContent={null}
        checkboxContent="Set automatically"
        inputContent={null}
        errorContent={null}
        className="u-input-spacing-top"
        key="header"
      />
      {props.regressionConfigState.parameterCustomizations.map(
        ({constraint, name, initialValue}, parameterIndex) => {
          function handleChange(newInitialValue: RegressionConfigModalParameterInitialValue): void {
            props.onChangeRegressionConfigState(
              produce(props.regressionConfigState, (draftState) => {
                draftState.parameterCustomizations[parameterIndex].initialValue = newInitialValue;
              })
            );
          }

          const isDisabled = constraint.type === RegressionParameterConstraintType.EQUAL;
          const checkbox = (
            <Tooltip
              text={isDisabled ? CANNOT_SET_INITIAL_VALUE_TOOLTIP : null}
              isDisabled={!isDisabled}
              disableContentSelection={true}
            >
              <CheckboxInput
                id={`${name}-initial-value-checkbox`}
                className={css(styles.checkboxInput)}
                label=""
                checked={!initialValue.userDefined}
                disabled={isDisabled}
                onChange={(e) => {
                  if (e.target.checked) {
                    handleChange({userDefined: false});
                  } else {
                    handleChange({
                      userDefined: true,
                      value: EMPTY_NUMERIC_INPUT_VALUE,
                    });
                  }
                }}
              />
            </Tooltip>
          );

          const customProps = CUSTOM_INPUT_PROPERTIES[name];
          const numericInput = (
            <IntermediateValueControlledNumericInput
              value={initialValue.userDefined ? initialValue.value.value : ''}
              onChange={(e) => {
                handleChange({
                  userDefined: true,
                  value: e.target,
                });
              }}
              isDisabled={!initialValue.userDefined}
              min={-Number.MAX_VALUE}
              max={Number.MAX_VALUE}
              {...customProps}
            />
          );

          const errorContent =
            initialValue.userDefined && !initialValue.value.isValid ? (
              <FieldError message={getParameterValueErrorMessage(name, initialValue.value.value)} />
            ) : null;

          return (
            <InitialValuesEditorRow
              labelContent={PARAMETER_NAME_TO_READABLE[name]}
              checkboxContent={checkbox}
              inputContent={numericInput}
              errorContent={errorContent}
              // u-input-spacing-top creates too large a gap between the header and first row
              className={parameterIndex === 0 ? 'u-small-spacing-top' : 'u-input-spacing-top'}
              key={name}
            />
          );
        }
      )}
    </>
  );
}

const styles = StyleSheet.create({
  rowWrapper: {
    display: 'flex',
  },
  label: {
    fontWeight: 'bold',
    width: 80,
  },
  checkbox: {
    width: 120,
    textAlign: 'center',
  },
  checkboxInput: {
    justifyContent: 'center',
  },
  input: {
    width: 120,
    marginLeft: 40,
  },
});
